import { useState } from "react";
import type { DevActionableNote } from "./devActionableNoteTypes";
import { noteAssetIncludeInSync, noteHasAsset, notePlacement } from "./devActionableNoteTypes";

type Props = {
  note: DevActionableNote;
  interactive?: boolean;
  includeInSync?: boolean;
  onIncludeInSyncChange?: (next: boolean) => void;
  onDelete?: () => void;
  deleting?: boolean;
  disabled?: boolean;
};

export function DevPanelActionableNoteAssetPreview({
  note,
  interactive = false,
  includeInSync,
  onIncludeInSyncChange,
  onDelete,
  deleting = false,
  disabled = false,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const hasAsset = noteHasAsset(note);
  const url = note.asset_preview_url?.trim();
  if (!hasAsset && !url) return null;

  const placement = notePlacement(note);
  const includeChecked = includeInSync ?? noteAssetIncludeInSync(note);

  return (
    <>
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-2 dark:border-indigo-800 dark:bg-indigo-950/30">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-900 dark:text-indigo-200">
            Attached graphic
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {url ? (
              <>
                <button
                  type="button"
                  className="text-[10px] font-medium text-indigo-700 underline dark:text-indigo-300"
                  onClick={() => setExpanded(true)}
                >
                  Expand
                </button>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] font-medium text-indigo-700 underline dark:text-indigo-300"
                >
                  Open full size
                </a>
              </>
            ) : (
              <span className="text-[10px] text-indigo-700/80 dark:text-indigo-300/80">
                Preview unavailable — refresh queue
              </span>
            )}
          </div>
        </div>

        {url ? (
          <button
            type="button"
            className="mt-2 flex max-h-56 w-full cursor-zoom-in items-center justify-center overflow-auto rounded-md border border-indigo-100 bg-white p-2 dark:border-indigo-900 dark:bg-zinc-950"
            onClick={() => setExpanded(true)}
            title="Click to expand"
          >
            <img
              src={url}
              alt="Dev sync graphic preview"
              className="max-h-48 max-w-full object-contain"
            />
          </button>
        ) : (
          <div className="mt-2 rounded-md border border-dashed border-indigo-200 bg-white/70 px-3 py-6 text-center text-[11px] text-indigo-800/80 dark:border-indigo-900 dark:bg-zinc-950 dark:text-indigo-200/80">
            Graphic is attached but preview URL expired or bucket is unavailable.
          </div>
        )}

        {interactive ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-[11px] text-indigo-950 dark:text-indigo-100">
              <input
                type="checkbox"
                checked={includeChecked}
                disabled={disabled || deleting || !hasAsset}
                onChange={(e) => onIncludeInSyncChange?.(e.target.checked)}
              />
              Ship graphic to repo on sync
            </label>
            <button
              type="button"
              className="rounded border border-red-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-red-800 hover:bg-red-50 disabled:opacity-50 dark:bg-zinc-900"
              disabled={disabled || deleting || !hasAsset}
              onClick={() => {
                if (window.confirm("Remove this graphic from the note? Storage copy will be deleted.")) {
                  onDelete?.();
                }
              }}
            >
              {deleting ? "Removing…" : "Remove graphic"}
            </button>
          </div>
        ) : null}

        {placement?.repo_path ? (
          <p className="mt-1.5 text-[10px] text-indigo-800/90 dark:text-indigo-200/90">
            Ships to{" "}
            <code className="rounded bg-white/80 px-0.5 dark:bg-zinc-900">{placement.repo_path}</code>
            {interactive && !includeChecked ? " when included in sync" : " on sync"}
          </p>
        ) : null}
      </div>

      {expanded && url ? (
        <div
          className="fixed inset-0 z-[9300] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Graphic preview"
          onClick={() => setExpanded(false)}
        >
          <div
            className="relative max-h-[92vh] max-w-[96vw] overflow-auto rounded-xl bg-white p-3 shadow-xl dark:bg-zinc-950"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">Attached graphic</p>
              <button
                type="button"
                className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200"
                onClick={() => setExpanded(false)}
              >
                Close
              </button>
            </div>
            <img src={url} alt="Dev sync graphic full preview" className="max-h-[80vh] max-w-full object-contain" />
          </div>
        </div>
      ) : null}
    </>
  );
}
